import { Box, Stack, Text } from '@chakra-ui/react'
import {
  faStar,
  faThumbsDown,
  faThumbsUp,
  type IconDefinition,
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import assertDefined from '@/utils/assertDefined'

import { useAppContext } from '@/client/context/app'
import { useUser } from '@/client/context/user'
import type { PostFeedbackRating } from '@/client/services/worlds/types'

import type { PostFeedbackDetails } from '@/components/Community/Post/types'
import MessageBox from '@/components/UI/MessageBox'
import DisplayNameLink from '@/components/Worlds/UI/DisplayNameLink'

const FEEDBACK_RATING_TO_BADGE_INFO: Record<
  PostFeedbackRating,
  { color: string; icon: IconDefinition }
> = {
  'Super Helpful': {
    color: 'blue.fg',
    icon: faStar,
  },
  Helpful: {
    color: 'green.fg',
    icon: faThumbsUp,
  },
  'Not Helpful': {
    color: 'fg.muted',
    icon: faThumbsDown,
  },
}

export default function PostFeedbackBanner({
  postFeedbacks,
}: {
  postFeedbacks: PostFeedbackDetails[]
}): JSX.Element | null {
  const currentUser = useUser()
  const renderContext = useAppContext()

  const feedbacksWithRating = postFeedbacks.filter(postFeedback => postFeedback.rating != null)

  if (!renderContext.dryRun && feedbacksWithRating.length === 0) {
    return null
  }

  return (
    <Stack width="100%" p="16px" bgColor="bg.muted" borderRadius="8px" spacing={6} my={4}>
      <Text variant="eyebrow">Post Feedback</Text>
      {feedbacksWithRating.map(postFeedback => {
        const { id, user, reviewer, note, rating } = postFeedback
        const isReviewee = user.id === currentUser.id
        const isReviewer = reviewer.id === currentUser.id

        assertDefined(rating, `Rating should be defined for PostFeedback#${id}`)
        const { color, icon } = FEEDBACK_RATING_TO_BADGE_INFO[rating]

        return (
          <Stack key={id} spacing={3}>
            <Text>
              {isReviewer ? (
                'You'
              ) : (
                <DisplayNameLink
                  userId={reviewer.id}
                  username={reviewer.username}
                  displayName={reviewer.displayName}
                  variant="subtle"
                />
              )}{' '}
              gave{' '}
              {isReviewee ? (
                'you'
              ) : (
                <DisplayNameLink
                  userId={user.id}
                  username={user.username}
                  displayName={user.displayName}
                  variant="subtle"
                />
              )}{' '}
              a{' '}
              <Box as="span" color={color} style={{ marginRight: 4 }}>
                <FontAwesomeIcon icon={icon} />
              </Box>
              <Box as="span" color={color}>
                {rating}{' '}
              </Box>
              rating
            </Text>
            {note != null ? <MessageBox text={note} bgColor="info.subtle" /> : null}
          </Stack>
        )
      })}
    </Stack>
  )
}
